// TUT-460 — as telas de entrada do app: o login do escritório e a entrada
// direta dos portais (advogado e assistente).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { LoginPage } from "./telas";
import { api } from "../lib/api";
import { PageLoading } from "../components/PageState";

/**
 * TUT-261 — a tela de login com endereco proprio.
 *
 * Ela e FINA de proposito: monta o mesmo `LoginPage` que o portao do escritorio
 * ja renderiza, e so acrescenta duas coisas, ambas no `LoginForaDoPortao`
 * abaixo: quem ja tem sessao volta para `/` sem ver o formulario, e para onde
 * ir depois de autenticar. Duplicar o formulario aqui criaria duas telas de
 * login para manter, e a divergencia entre copias e o defeito que esta casa ja
 * pagou duas vezes (o helper de navegacao do e2e, os tres quadros da
 * Elaboracao).
 *
 * **Vai para `/`, e isso importa para as provas:** ate esta fatia, 26 specs
 * faziam `goto("/")`, preenchiam e seguiam trabalhando na Visao Geral. Com o
 * login em `/entrar` e a volta para `/`, cada uma trocou UMA linha e a jornada
 * continua a mesma — o destino nao mudou, so a porta.
 *
 * Quem chega deslogado numa tela de trabalho NAO passa por aqui: continua vendo
 * o formulario no lugar da rota pedida, sem redirecionar, e depois de entrar
 * esta onde queria estar. Essa decisao e anterior a esta fatia e e melhor que
 * redirecionar com parametro de retorno.
 */
export function EntrarScreen() {
  return <LoginForaDoPortao />;
}

/**
 * TUT-308 — os endereços diretos das duas entradas externas. Vão para o
 * mesmo login de sempre, só com a entrada certa já escolhida (o convite e o
 * botão "Portal do advogado"/"Portal do assistente" apontam para cá em vez de
 * `/entrar?entrada=...` — mais curto, e sobrevive à troca de copy da URL).
 *
 * A ESCOLHA NUNCA decide acesso: se a sessão já existe, quem decide o destino
 * é o mesmo portão de sempre (`TenantGate`, via bifurcação por `roleKey`) —
 * aqui só devolve para `/` e sai da frente. É por isso que a consulta de
 * sessão usa a MESMA chave (`["session"]`): TenantGate reaproveita o cache,
 * não refaz a requisição.
 */
export function EntradaDiretaScreen({ entrada }: { entrada: "advogado" | "assistente" }) {
  return <LoginForaDoPortao entradaInicial={entrada} />;
}

/**
 * O login das rotas públicas (`/entrar` e os endereços diretos dos portais).
 * Com sessão ativa não há o que pedir: devolve para `/` e o portão decide.
 * TUT-472 — `/entrar` não consultava a sessão e mostrava o formulário vazio
 * a quem já estava dentro, enquanto `/portal/advogado` já redirecionava; as
 * duas entradas passam pelo MESMO componente para não divergirem de novo.
 */
function LoginForaDoPortao({
  entradaInicial,
}: {
  entradaInicial?: "advogado" | "assistente";
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const session = useQuery({
    queryKey: ["session"],
    queryFn: api.session,
    retry: false,
    staleTime: 60_000,
  });

  if (session.isLoading) return <PageLoading label="Verificando seu acesso..." />;
  if (session.data) return <Navigate to="/" />;

  return (
    <LoginPage
      entradaInicial={entradaInicial}
      onAuthenticated={(value) => {
        queryClient.setQueryData(["session"], value);
        void navigate({ to: "/" });
      }}
    />
  );
}
